import React from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import * as Icons from "lucide-react";
import { HelpCircle } from "lucide-react";
import { MODULES, TID } from "@/constants/testIds";
import { useAuth } from "@/lib/auth";
import { BRAND } from "@/config/brand";

export default function Sidebar({ collapsed = false }) {
  const location = useLocation();
  const { user } = useAuth();
  const isPlatformStaff = ["founder", "platform_admin"].includes(user?.platform_role);
  const LogoSymbol = BRAND.logo.Symbol;
  return (
    <aside
      data-testid={TID.sidebar}
      className="hidden lg:flex fixed inset-y-0 left-0 z-40 w-64 flex-col"
      style={{ background: BRAND.colors.night }}
    >
      <div className="flex items-center gap-2.5 px-6 pt-7 pb-8">
        <LogoSymbol size={36} tone="light" />
        <div className="flex flex-col leading-tight">
          <span className="text-xl text-white" style={{
            fontFamily: "'Cabinet Grotesk', 'Space Grotesk', system-ui, sans-serif",
            fontWeight: 700, letterSpacing: "0.02em",
          }}>{BRAND.nameUppercase}</span>
          <span className="text-[10px] uppercase tracking-[0.2em]"
                style={{ color: "rgba(255,255,255,0.55)" }}>
            Gestion immobilière
          </span>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 pb-6">
        <div className="mb-2 px-3 text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: "hsl(40 15% 65%)" }}>
          Navigation
        </div>
        <ul className="space-y-0.5">
          {MODULES.map((m) => {
            const Icon = Icons[m.icon] || Icons.Circle;
            const isActive = location.pathname.startsWith(m.path);
            if (!m.available) {
              return (
                <li key={m.key}>
                  <div className={`sidebar-nav-item disabled justify-between`} data-testid={TID.sidebarLink(m.key)}>
                    <span className="flex items-center gap-3">
                      <Icon className="h-4 w-4" />
                      {m.label}
                    </span>
                    <span className="rounded-full px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider"
                          style={{ background: "hsl(158 47% 20%)", color: "hsl(36 43% 60%)" }}>Bientôt</span>
                  </div>
                </li>
              );
            }
            return (
              <li key={m.key}>
                <NavLink to={m.path} data-testid={TID.sidebarLink(m.key)}
                         className={({ isActive: a }) => `sidebar-nav-item ${a ? "active" : ""}`}>
                  <Icon className="h-4 w-4" />
                  <span>{m.label}</span>
                </NavLink>
              </li>
            );
          })}
        </ul>

        {isPlatformStaff && (
          <>
            <div className="mt-6 mb-2 px-3 text-[10px] font-bold uppercase tracking-[0.2em]"
                 style={{ color: "hsl(36 43% 60%)" }}>
              Plateforme
            </div>
            <ul className="space-y-0.5">
              <li>
                <NavLink to="/admin"
                         data-testid="sidebar-link-platform-admin"
                         className={({ isActive: a }) => `sidebar-nav-item ${a ? "active" : ""}`}>
                  <Icons.Shield className="h-4 w-4" />
                  <span>Back-office</span>
                </NavLink>
              </li>
            </ul>
          </>
        )}
      </nav>

      <div className="px-6 py-5 text-[11px]" style={{ color: "hsl(40 15% 65%)" }}>
        <Link
          to="/app/help"
          data-testid="sidebar-help-link"
          className="block rounded-xl border p-3 hover:brightness-110 transition"
          style={{ borderColor: "hsl(158 47% 22%)", background: "hsl(158 47% 15%)" }}
        >
          <div className="font-heading text-xs text-white flex items-center gap-1.5">
            <HelpCircle className="h-3 w-3" /> Centre d&apos;aide
          </div>
          <div className="mt-1">Guides pas à pas, FAQ, et fiches par module.</div>
        </Link>
      </div>
    </aside>
  );
}
